import { CommonModule } from '@angular/common';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { Component, inject, ViewChild, OnInit } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  InputComponent,
  SelectComponent,
  SelectOption,
  ToastComponent,
  ToastService,
  ProgressStepsComponent,
  ProgressStep,
} from '@/shared/components';
import { CepService } from '@/services/cep.service';
import { AuthService, CandidateRegisterRequest } from '@/services/auth.service';
import { GeoService } from '@/services';
import { CandidateService, CandidateData, CandidateUpdateRequest } from '@/services/candidate.service';
import { forkJoin } from 'rxjs';
import { City, State } from '@/models';
import { CpfValidator } from '@/shared/validators';
import { EmailValidator } from '@/shared/validators/email.validator';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    HttpClientModule,
    InputComponent,
    SelectComponent,
    ToastComponent,
    ProgressStepsComponent,
  ],
  templateUrl: './register.component.html',
})
export class RegisterComponent implements OnInit {
  @ViewChild('registerForm') registerForm!: NgForm;

  currentStep = 1;
  get totalSteps(): number {
    return this.isEditMode ? 3 : 4; // Em modo edição, não tem passo de senha
  }
  submitted = false;
  isEditMode = false;
  originalData: CandidateData | null = null;
  isLoadingData = false;

  formData = {
    // Passo 1: Identificação
    nomeCompleto: '',
    sexo: '',
    usarNomeSocial: 'nao',
    nomeSocial: '',
    diaNascimento: '',
    mesNascimento: '',
    anoNascimento: '',
    cpf: '',
    rg: '',
    nomePai: '',
    profissaoPai: '',
    nomeMae: '',
    nacionalidade: '',
    estadoCivil: '',
    escolaridade: '',
    numeroDependentes: 0,
    telefone: '',
    outroTelefone: '',
    // Passo 2: Endereço
    cep: '',
    estadoId: '',
    cidadeId: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    // Passo 3: Conta
    email: '',
    emailConfirmacao: '',
    emailAlternativo: '',
    senha: '',
    senhaConfirmacao: '',
    // Passo 4: Confirmação
    receberInformacoes: false,
    aceitaTermos: false,
  };

  // Progress steps configuration
  get progressSteps(): ProgressStep[] {
    if (this.isEditMode) {
      return [
        { number: 1, title: 'Identificação' },
        { number: 2, title: 'Endereço' },
        { number: 3, title: 'Confirmação' },
      ];
    }
    return [
      { number: 1, title: 'Identificação' },
      { number: 2, title: 'Endereço' },
      { number: 3, title: 'Conta' },
      { number: 4, title: 'Confirmação' },
    ];
  }

  get warningSteps(): number[] {
    const warnings: number[] = [];
    if (!this.validateCurrentStep() && this.currentStep !== this.totalSteps) {
      warnings.push(this.currentStep);
    }
    return warnings;
  }

  get errorStep(): number | undefined {
    if (this.submitted && !this.validateCurrentStep()) {
      return this.currentStep;
    }
    return undefined;
  }

  get hasError(): boolean {
    return this.submitted && !this.validateCurrentStep();
  }

  get disabledSteps(): number[] {
    // No modo registro, não pode pular etapas. Em modo edição também.
    // Só pode acessar etapas anteriores ou a próxima se a atual estiver válida
    const disabled: number[] = [];
    for (let i = this.currentStep + 2; i <= this.totalSteps; i++) {
      disabled.push(i);
    }
    return disabled;
  }

  showPassword = false;
  showPasswordConfirm = false;
  isLoading = false;
  successMessage = false;
  cepLoading = false;
  cepError = '';

  // Arrays para os selects
  // convert to select options in app-select
  dias: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString(),
  }));

  sexoOptions: SelectOption[] = [
    { value: 'MASCULINO', label: 'Masculino' },
    { value: 'FEMININO', label: 'Feminino' },
    { value: 'OUTRO', label: 'Outro' },
    { value: 'NAO_INFORMAR', label: 'Prefiro não informar' },
  ];

  ufOptions: SelectOption[] = [
    { value: 'AC', label: 'AC' },
    { value: 'AL', label: 'AL' },
    { value: 'AP', label: 'AP' },
    { value: 'AM', label: 'AM' },
    { value: 'BA', label: 'BA' },
    { value: 'CE', label: 'CE' },
    { value: 'DF', label: 'DF' },
    { value: 'ES', label: 'ES' },
    { value: 'GO', label: 'GO' },
    { value: 'MA', label: 'MA' },
    { value: 'MT', label: 'MT' },
    { value: 'MS', label: 'MS' },
    { value: 'MG', label: 'MG' },
    { value: 'PA', label: 'PA' },
    { value: 'PB', label: 'PB' },
    { value: 'PR', label: 'PR' },
    { value: 'PE', label: 'PE' },
    { value: 'PI', label: 'PI' },
    { value: 'RJ', label: 'RJ' },
    { value: 'RN', label: 'RN' },
    { value: 'RS', label: 'RS' },
    { value: 'RO', label: 'RO' },
    { value: 'RR', label: 'RR' },
    { value: 'SC', label: 'SC' },
    { value: 'SP', label: 'SP' },
    { value: 'SE', label: 'SE' },
    { value: 'TO', label: 'TO' },
  ];

  estadoOptions: SelectOption[] = [];
  cidadeOptions: SelectOption[] = [];
  allCidades: City[] = [];
  allEstados: State[] = [];

  nacionalidadeOptions: SelectOption[] = [
    { value: 'brasileira', label: 'Brasileira' },
    { value: 'naturalizado', label: 'Naturalizado' },
    { value: 'estrangeiro', label: 'Estrangeiro' },
  ];

  estadoCivilOptions: SelectOption[] = [
    { value: 'SOLTEIRO', label: 'Solteiro(a)' },
    { value: 'CASADO', label: 'Casado(a)' },
    { value: 'DIVORCIADO', label: 'Divorciado(a)' },
    { value: 'VIUVO', label: 'Viúvo(a)' },
    { value: 'UNIAO_ESTAVEL', label: 'União Estável' },
    { value: 'OUTRO', label: 'Outro' },
    { value: 'NAO_INFORMAR', label: 'Prefiro não informar' },
  ];

  escolaridadeOptions: SelectOption[] = [
    { value: 'FUNDAMENTAL', label: 'Ensino Fundamental' },
    { value: 'MEDIO', label: 'Ensino Médio' },
    { value: 'TECNICO', label: 'Técnico' },
    { value: 'SUPERIOR', label: 'Ensino Superior' },
    { value: 'POS', label: 'Pós-Graduação' },
    { value: 'MESTRADO', label: 'Mestrado' },
    { value: 'DOUTORADO', label: 'Doutorado' },
    { value: 'OUTRO', label: 'Outro' },
    { value: 'NAO_INFORMAR', label: 'Prefiro não informar' },
  ];

  meses: SelectOption[] = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  anos: SelectOption[] = (() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push({ value: year, label: year.toString() });
    }
    return years;
  })();

  private router = inject(Router);
  private http = inject(HttpClient);
  private cepService = inject(CepService);
  private authService = inject(AuthService);
  private candidateService = inject(CandidateService);

  constructor(
    private route: ActivatedRoute,
    private geoService: GeoService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    // Verificar se está em modo edição (rota /portal/candidato/meus-dados)
    this.route.url.subscribe(segments => {
      const path = segments.map(s => s.path).join('/');
      this.isEditMode = path.includes('meus-dados');
      
      if (this.isEditMode) {
        this.loadCandidateData();
      } else {
        // Em modo registro, carregar estados e cidades normalmente
        this.loadEstadosAndCidades();
      }
    });
  }

  loadCandidateData(): void {
    this.isLoadingData = true;
    this.candidateService.getMe().subscribe({
      next: (data) => {
        this.originalData = data;
        this.populateFormFromData(data);
        this.isLoadingData = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados do candidato:', error);
        const errorMessage = error.error?.message || 'Erro ao carregar seus dados';
        this.toastService.error(errorMessage);
        this.isLoadingData = false;
      }
    });
  }

  populateFormFromData(data: CandidateData): void {
    // Parse dataNascimento (YYYY-MM-DD)
    const dateParts = data.dataNascimento.split('-');
    this.formData.anoNascimento = dateParts[0];
    this.formData.mesNascimento = dateParts[1];
    this.formData.diaNascimento = dateParts[2];

    this.formData.nomeCompleto = data.nomeCompleto;
    this.formData.sexo = data.sexo;
    this.formData.usarNomeSocial = data.desejaNomeSocial ? 'sim' : 'nao';
    this.formData.nomeSocial = data.nomeSocial || '';
    this.formData.cpf = data.cpf;
    this.formData.rg = data.rg || '';
    this.formData.nomePai = data.nomePai || '';
    this.formData.profissaoPai = data.profissaoPai || '';
    this.formData.nomeMae = data.nomeMae;
    this.formData.nacionalidade = data.nacionalidade;
    this.formData.estadoCivil = data.estadoCivil;
    this.formData.escolaridade = data.nivelEscolaridade;
    this.formData.numeroDependentes = data.numeroDependentes;
    this.formData.telefone = data.telefonePessoal || '';
    this.formData.outroTelefone = data.outroTelefone || '';
    this.formData.email = data.email;
    this.formData.emailConfirmacao = data.email;
    this.formData.emailAlternativo = data.emailAlternativo || '';
    this.formData.receberInformacoes = data.receberInfoOrganizadora;
    this.formData.aceitaTermos = data.declaracaoCanaisAtualizados;
    
    // Endereço - usar endereco se disponível, caso contrário logradouro
    if (data.cidadeId) {
      this.formData.cidadeId = data.cidadeId.toString();
    }
    if (data.estadoId) {
      this.formData.estadoId = data.estadoId.toString();
    }
    this.formData.cep = data.cep || '';
    this.formData.endereco = data.endereco || data.logradouro || '';
    this.formData.numero = data.numero || '';
    this.formData.complemento = data.complemento || '';
    this.formData.bairro = data.bairro || '';

    // Carregar estados e cidades, depois selecionar o estado baseado na cidade ou estadoId
    this.loadEstadosAndCidades();
  }

  loadEstadosAndCidades(): void {
    if (this.isLoading) return; // Evitar múltiplas chamadas simultâneas
    this.isLoading = true;
    forkJoin({
      estados: this.geoService.getEstados(),
      cidades: this.geoService.getCidades(),
    }).subscribe({
      next: ({ estados, cidades }) => {
        this.allEstados = estados;
        this.allCidades = cidades;

        this.estadoOptions = [
          { value: '', label: 'Selecione o estado' },
          ...estados.map((estado) => ({
            value: estado.id.toString(),
            label: `${estado.nome} (${estado.uf})`,
          })),
        ];

        // Se estamos em modo edição e temos estadoId ou cidadeId, configurar
        if (this.isEditMode && this.originalData) {
          if (this.originalData.estadoId) {
            // Se temos estadoId direto, usar ele
            this.formData.estadoId = this.originalData.estadoId.toString();
            this.updateCidadeOptions();
          } else if (this.formData.cidadeId) {
            // Caso contrário, encontrar o estado pela cidade
            const cidade = cidades.find(c => c.id === parseInt(this.formData.cidadeId));
            if (cidade) {
              this.formData.estadoId = cidade.estadoId.toString();
              this.updateCidadeOptions();
            }
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading geographic data:', error);
        const errorMessage = error.error?.message || 'Erro ao carregar dados geográficos';
        this.toastService.error(errorMessage);
        this.isLoading = false;
      },
    });
  }


  onEstadoChange(): void {
    this.formData.cidadeId = '';
    this.updateCidadeOptions();
  }

  updateCidadeOptions(): void {
    if (!this.formData.estadoId) {
      this.cidadeOptions = [{ value: '', label: 'Selecione a cidade' }];
      return;
    }

    const estadoId = parseInt(this.formData.estadoId);
    const filteredCidades = this.allCidades.filter(
      (c) => c.estadoId === estadoId
    );

    this.cidadeOptions = [
      { value: '', label: 'Selecione a cidade' },
      ...filteredCidades.map((cidade) => ({
        value: cidade.id.toString(),
        label: cidade.nome,
      })),
    ];
  }

  getCpfError(cpfControl: any): string {
    if (!cpfControl) return '';

    // Show required error only after submit
    if (cpfControl.invalid && cpfControl.errors?.['required']) {
      if (this.submitted) {
        return 'CPF é obrigatório';
      }
      return '';
    }

    // Show CPF validation error if field was touched and has value
    if (this.formData.cpf && (cpfControl.touched || this.submitted)) {
      if (!CpfValidator.isValid(this.formData.cpf)) {
        return CpfValidator.getErrorMessage();
      }
    }

    return '';
  }

  getEmailError(): string | null {
    const email = this.formData.email;
    if (!email) return null;
    if (!EmailValidator.isValid(email)) {
      return EmailValidator.getErrorMessage();
    }
    return null;
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  togglePasswordConfirm() {
    this.showPasswordConfirm = !this.showPasswordConfirm;
  }

  getCidadeNome(): string {
    if (!this.formData.cidadeId) return '-';
    const cidade = this.allCidades.find(c => c.id === parseInt(this.formData.cidadeId));
    return cidade ? cidade.nome : '-';
  }

  getEstadoUF(): string {
    if (!this.formData.estadoId) return '-';
    const estado = this.allEstados.find(e => e.id === parseInt(this.formData.estadoId));
    return estado ? estado.uf : '-';
  }

  buscarCep() {
    const cep = this.formData.cep.replace(/\D/g, '');

    if (cep.length !== 8) {
      return;
    }

    this.cepLoading = true;
    this.cepError = '';

    this.cepService.buscarCep(cep).subscribe({
      next: (data) => {
        this.cepLoading = false;

        // Preenche os campos automaticamente
        this.formData.endereco = data.logradouro;
        this.formData.bairro = data.bairro;

        // Find estado by UF in the allEstados array
        const estado = this.allEstados.find(e => e.uf === data.uf);

        if (estado) {
          this.formData.estadoId = estado.id.toString();
          this.updateCidadeOptions();

          // Wait for cidade options to update, then find and select cidade
          setTimeout(() => {
            const cidade = this.allCidades.find(c =>
              c.estadoId === estado.id &&
              c.nome.toLowerCase() === data.cidade.toLowerCase()
            );
            if (cidade) {
              this.formData.cidadeId = cidade.id.toString();
            }
          }, 100);
        }
      },
      error: () => {
        this.cepLoading = false;
        this.cepError = 'Erro ao buscar CEP';
      },
    });
  }

  nextStep() {
    this.submitted = true;

    if (!this.validateCurrentStep()) {
      return;
    }

    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.submitted = false;
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.submitted = false;
    }
  }

  setStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // Só permite ir para etapas anteriores ou para a próxima se a atual estiver válida
      if (step < this.currentStep || (step === this.currentStep + 1 && this.validateCurrentStep())) {
        this.currentStep = step;
        this.submitted = false;
      }
    }
  }

  validateCurrentStep(): boolean {
    const data = this.formData;

    switch (this.currentStep) {
      case 1: // Identificação
        const step1Valid =
          data.nomeCompleto.trim() !== '' &&
          data.sexo !== '' &&
          (this.isEditMode || (data.diaNascimento !== '' && data.mesNascimento !== '' && data.anoNascimento !== '')) &&
          (this.isEditMode || data.cpf.trim() !== '') &&
          data.rg.trim() !== '' &&
          data.nomeMae.trim() !== '' &&
          data.nacionalidade !== '' &&
          data.estadoCivil !== '' &&
          data.escolaridade !== '' &&
          data.telefone.trim() !== '' &&
          (data.usarNomeSocial !== 'sim' || data.nomeSocial.trim() !== '');
        return step1Valid;

      case 2: // Endereço
        const step2Valid =
          data.cep.trim() !== '' &&
          data.estadoId !== '' &&
          data.cidadeId !== '' &&
          data.endereco.trim() !== '' &&
          data.bairro.trim() !== '' &&
          data.numero.trim() !== '';
        return step2Valid;

      case 3: // Conta (no modo edição, não validamos senha)
        if (this.isEditMode) {
          // No modo edição, apenas validamos o email (emailConfirmacao está oculto)
          const step3Valid = data.email.trim() !== '';
          return step3Valid;
        } else {
          const step3Valid =
            data.email.trim() !== '' &&
            data.emailConfirmacao.trim() !== '' &&
            data.senha.trim() !== '' &&
            data.senhaConfirmacao.trim() !== '' &&
            data.email === data.emailConfirmacao &&
            data.senha === data.senhaConfirmacao;
          return step3Valid;
        }

      case 4: // Confirmação (apenas em modo registro)
        return data.aceitaTermos;

      default:
        return true;
    }
  }

  hasChanges(): boolean {
    if (!this.originalData) return true; // Se não tem dados originais, sempre salva

    const original = this.originalData;
    const current = this.formData;

    // Comparar todos os campos editáveis (exceto nome, CPF e dataNascimento que são readonly)
    return (
      original.sexo !== current.sexo ||
      original.desejaNomeSocial !== (current.usarNomeSocial === 'sim') ||
      (original.nomeSocial || '') !== (current.nomeSocial || '') ||
      original.rg !== (current.rg || '') ||
      (original.nomePai || '') !== (current.nomePai || '') ||
      (original.profissaoPai || '') !== (current.profissaoPai || '') ||
      original.nomeMae !== current.nomeMae ||
      original.nacionalidade !== current.nacionalidade ||
      original.estadoCivil !== current.estadoCivil ||
      original.nivelEscolaridade !== current.escolaridade ||
      original.numeroDependentes !== current.numeroDependentes ||
      original.telefonePessoal !== current.telefone ||
      (original.outroTelefone || '') !== (current.outroTelefone || '') ||
      (original.emailAlternativo || '') !== (current.emailAlternativo || '') ||
      original.receberInfoOrganizadora !== current.receberInformacoes ||
      original.declaracaoCanaisAtualizados !== current.aceitaTermos ||
      (original.cidadeId?.toString() || '') !== (current.cidadeId || '') ||
      (original.estadoId?.toString() || '') !== (current.estadoId || '') ||
      (original.cep || '') !== (current.cep || '') ||
      ((original.endereco || original.logradouro || '') !== (current.endereco || '')) ||
      (original.numero || '') !== (current.numero || '') ||
      (original.complemento || '') !== (current.complemento || '') ||
      (original.bairro || '') !== (current.bairro || '')
    );
  }

  onSubmit() {
    this.submitted = true;

    if (this.currentStep < this.totalSteps) {
      this.nextStep();
    } else {
      // Último passo - submeter cadastro ou edição
      if (!this.validateCurrentStep()) {
        return;
      }

      // Em modo edição, verificar se houve mudanças
      if (this.isEditMode && !this.hasChanges()) {
        this.toastService.success('Nenhuma alteração foi detectada.');
        return;
      }

      this.isLoading = true;

      if (this.isEditMode) {
        // Modo edição - usar PUT /api/candidatos/me
        const updateData: CandidateUpdateRequest = {
          nomeCompleto: this.originalData!.nomeCompleto, // Não editável, mas precisa enviar
          sexo: this.formData.sexo as any,
          desejaNomeSocial: this.formData.usarNomeSocial === 'sim',
          nomeSocial:
            this.formData.usarNomeSocial === 'sim'
              ? this.formData.nomeSocial
              : undefined,
          dataNascimento: this.originalData!.dataNascimento, // Não editável, mas precisa enviar
          rg: this.formData.rg,
          nomePai: this.formData.nomePai || undefined,
          profissaoPai: this.formData.profissaoPai || undefined,
          nomeMae: this.formData.nomeMae,
          nacionalidade: this.formData.nacionalidade,
          estadoCivil: this.formData.estadoCivil as any,
          nivelEscolaridade: this.formData.escolaridade as any,
          numeroDependentes: this.formData.numeroDependentes,
          telefonePessoal: this.formData.telefone.replace(/\D/g, ''),
          outroTelefone: this.formData.outroTelefone || undefined,
          emailAlternativo: this.formData.emailAlternativo || undefined,
          receberInfoOrganizadora: this.formData.receberInformacoes,
          declaracaoCanaisAtualizados: this.formData.aceitaTermos,
          cidadeId: this.formData.cidadeId ? parseInt(this.formData.cidadeId) : undefined,
          cep: this.formData.cep.replace(/\D/g, ''),
          logradouro: this.formData.endereco,
          numero: this.formData.numero,
          semNumero: false,
          complemento: this.formData.complemento || undefined,
          bairro: this.formData.bairro,
        };

        this.candidateService.updateMe(updateData).subscribe({
          next: (response) => {
            this.toastService.success('Dados atualizados com sucesso!');
            this.isLoading = false;
            // Recarregar dados atualizados
            this.loadCandidateData();
          },
          error: (error) => {
            console.error('Erro ao atualizar dados:', error);
            const errorMessage = error.error?.message || 'Erro ao atualizar seus dados. Por favor, tente novamente.';
            this.toastService.error(errorMessage);
            this.isLoading = false;
          },
        });
      } else {
        // Modo registro - usar POST /api/auth/register
        const registerData: CandidateRegisterRequest = {
          cpf: this.formData.cpf.replace(/\D/g, ''),
          nomeCompleto: this.formData.nomeCompleto,
          sexo: this.formData.sexo as any,
          desejaNomeSocial: this.formData.usarNomeSocial === 'sim',
          nomeSocial:
            this.formData.usarNomeSocial === 'sim'
              ? this.formData.nomeSocial
              : undefined,
          dataNascimento: `${this.formData.anoNascimento}-${
            this.formData.mesNascimento
          }-${String(this.formData.diaNascimento).padStart(2, '0')}`,
          rg: this.formData.rg,
          nomePai: this.formData.nomePai || undefined,
          nomeMae: this.formData.nomeMae,
          nacionalidade: this.formData.nacionalidade,
          estadoCivil: this.formData.estadoCivil as any,
          nivelEscolaridade: this.formData.escolaridade as any,
          numeroDependentes: this.formData.numeroDependentes,
          telefonePessoal: this.formData.telefone.replace(/\D/g, ''),
          email: this.formData.email,
          emailConfirm: this.formData.emailConfirmacao,
          emailAlternativo: this.formData.emailAlternativo || undefined,
          senha: this.formData.senha,
          senhaConfirm: this.formData.senhaConfirmacao,
          termosAceitos: this.formData.aceitaTermos,
          receberInfoOrganizadora: this.formData.receberInformacoes,
          declaracaoCanaisAtualizados: this.formData.aceitaTermos,
          cidadeId: parseInt(this.formData.cidadeId),
          cep: this.formData.cep.replace(/\D/g, ''),
          logradouro: this.formData.endereco,
          numero: this.formData.numero,
          semNumero: false,
          complemento: this.formData.complemento || undefined,
          bairro: this.formData.bairro,
        };

        this.authService.registerCandidate(registerData).subscribe({
          next: (response) => {
            this.successMessage = true;

            // Fazer login automático após cadastro bem-sucedido
            this.authService.login({
              email: this.formData.email,
              password: this.formData.senha
            }).subscribe({
              next: (loginResponse) => {
                this.isLoading = false;
                const user = this.authService.getCurrentUser();
                if (user) {
                  const redirectPath = this.authService.getRedirectPath(user.role);
                  this.router.navigate([redirectPath]);
                } else {
                  this.router.navigate(['/portal/candidato']);
                }
              },
              error: (loginError) => {
                console.error('Erro no login automático:', loginError);
                const errorMessage = loginError.error?.message || 'Erro ao fazer login';
                this.toastService.error(errorMessage);
                this.isLoading = false;
                // Se login automático falhar, redireciona para página de login
                setTimeout(() => {
                  this.router.navigate(['/login']);
                }, 2000);
              }
            });
          },
          error: (error) => {
            console.error('Erro ao cadastrar:', error);
            const errorMessage = error.error?.message || 'Erro ao realizar cadastro. Por favor, tente novamente.';
            this.toastService.error(errorMessage);
            this.isLoading = false;
          },
        });
      }
    }
  }
}
